import React from 'react';
import './Header.css';

import FullscreenExitIcon from '../../assets/Icons/FullscreenExitIcon.svg?react';

import type { UserDto as User } from '@poker-stats/contracts';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setPortableMode } from '../../containers/App/actions';
import useSearchPlayers from '../../hooks/useSearchPlayers';
import NavMenu from '../NavMenu/NavMenu';
import UserMenu from '../UserMenu/UserMenu';
import Randomizer from '../Randomizer/Randomizer';
import AutopasteSwitch from '../AutopasteSwitch/AutopasteSwitch';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import { useTranslation } from 'react-i18next';
import Flex from '../Flex/Flex';
import { Select } from 'antd';
import type { RefSelectProps } from 'antd/es/select';
import PlayerSearchOptionLink from '../PlayerSearchOptionLink/PlayerSearchOptionLink';
import type { PlayerSearchOption } from '../../hooks/useSearchPlayers';

type Props = {
  user: User;
};

const Header = React.memo(({ user }: Props) => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const playerSearchRef = React.useRef<RefSelectProps>(null);

  const {
    nicknameToFind,
    searchPlayer,
    focusPlayerSearch,
    blurPlayerSearch,
    clearAutocompletePlayers,
    selectPlayer,
    selectPlayerOption,
    options,
  } = useSearchPlayers();

  const handleOpenPortableMode = React.useCallback(() => {
    dispatch(setPortableMode(true));
  }, [dispatch]);

  const handleSelectPlayerOption = React.useCallback(
    (option: PlayerSearchOption) => {
      selectPlayerOption(option.player);
      playerSearchRef.current?.blur();
    },
    [selectPlayerOption],
  );

  return (
    <header className="Header">
      <Flex className="Header__Content" align="center" gap={8}>
        <div className="Header__Logo" onClick={() => navigate('/statistics')} />
        <Flex className="Header__Search" align="center" gap={8} flex>
          <Select<number, PlayerSearchOption>
            className="Header__Select"
            ref={playerSearchRef}
            options={options}
            placeholder={t('search_for_players')}
            showSearch={{
              onSearch: searchPlayer,
              searchValue: nicknameToFind,
              filterOption: false,
            }}
            onChange={selectPlayer}
            value={null}
            onOpenChange={(open) => {
              if (!open) {
                clearAutocompletePlayers();
              }
            }}
            notFoundContent={nicknameToFind === '' ? null : undefined}
            onFocus={focusPlayerSearch}
            onBlur={blurPlayerSearch}
            optionRender={(option) => (
              <PlayerSearchOptionLink option={option.data} onSelect={handleSelectPlayerOption} />
            )}
          />
          <i className="Header__FullscreenExitIcon" onClick={handleOpenPortableMode}>
            <FullscreenExitIcon />
          </i>
        </Flex>
        <Flex className="Header__Aside" align="center" gap={8}>
          <Randomizer />
          <AutopasteSwitch />
          <NavMenu />
          <Flex className="Header__Account" align="center" gap={12}>
            <LanguageSelector />
            <UserMenu user={user} />
          </Flex>
        </Flex>
      </Flex>
    </header>
  );
});

export default Header;
